import React from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { getSettings } from "src/services/configService";
import { useSelector } from "src/store";
import { selectIsWorkspaceResolved } from "src/store/app";
import { selectHasValidSession } from "src/store/auth";
import { useEnrollmentFlowEnabled } from "src/ui/containers/dashboard/enrollment/utils/useEnrollmentFlowEnabled";
import { isDashboardPageAccessible } from "src/ui/navigation/dashboardRouting/dashboardRouting";
import { useRecommendedPathForUser } from "src/ui/navigation/dashboardRouting/useRecommendedPathForUser";

export const PageGuard = () => {
    const location = useLocation();
    const pathname = location.pathname;
    const { features } = getSettings();
    const { effectiveWorkspaceSettings, isLoading } = useAppSettingsService();
    const isEnrollmentFlowEnabled = useEnrollmentFlowEnabled();
    const hasValidSession = useSelector(selectHasValidSession);
    const isWorkspaceResolved = useSelector(selectIsWorkspaceResolved);
    const { path: recommendedPath, isLoading: isRecommendedPathLoading } = useRecommendedPathForUser();
    const accessContext = { effective: effectiveWorkspaceSettings, pages: features.pages };

    if (pathname === "/register" && !features.registrationEnabled) {
        return <Navigate to="/login" replace />;
    }

    if (pathname === "/dashboard/enrollment" && isEnrollmentFlowEnabled) {
        return <Outlet />;
    }

    if (pathname === "/embedded/third-party" && !features.pages.ossLicensesPageEnabled) {
        return <Navigate to="/forbidden" replace />;
    }

    const isAccessible = isDashboardPageAccessible(pathname, accessContext);

    const isIndexPath = pathname === "/" || pathname === "/dashboard";
    const honorsRecommendation = isIndexPath || isAccessible === false;

    if (honorsRecommendation && hasValidSession) {
        if (!isWorkspaceResolved || isRecommendedPathLoading) {
            return <Outlet />;
        }
        if (recommendedPath !== pathname) {
            return <Navigate to={recommendedPath} replace />;
        }
        return <Outlet />;
    }

    if (isAccessible === null) {
        return <Outlet />;
    }

    if (isLoading || isAccessible) {
        return <Outlet />;
    }

    return <Navigate to="/dashboard/overview" replace />;
};
